import React, { useState, useMemo } from 'react';

export default function DocumentViewerModal({ isOpen, onClose, documents, doctorName }) {
  const [currentDocIndex, setCurrentDocIndex] = useState(0);

  // ✅ useMemo must be called before any conditional return
  const documentList = useMemo(() => {
    if (!documents) return [];

    // Case 1: Array
    if (Array.isArray(documents)) {
      return documents.map((doc) =>
        typeof doc === "object" && doc.url ? doc.url : doc
      );
    }

    // Case 2: Object with keys (id_proof, etc.)
    if (typeof documents === "object") {
      return Object.values(documents)
        .filter((doc) => doc && (doc.url || typeof doc === "string"))
        .map((doc) => (typeof doc === "object" ? doc.url : doc));
    }

    // Case 3: Single string
    return [documents];
  }, [documents]);

  if (!isOpen) return null;

  const hasMultipleDocs = documentList.length > 1;

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          animation: "fadeIn 0.2s ease-out",
        }}
      >
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          }}
        />

        {/* Modal Content */}
        <div
          style={{
            position: "relative",
            backgroundColor: "white",
            borderRadius: "1rem",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            width: "100%",
            maxWidth: "72rem",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            animation: "slideUp 0.3s ease-out",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1rem 1.5rem",
              borderBottom: "1px solid #e5e7eb",
              background: "linear-gradient(to right, #fce7f3, #faf5ff)",
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#1f2937",
                  margin: 0,
                }}
              >
                Medical Documents
              </h2>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  marginTop: "0.25rem",
                  marginBottom: 0,
                }}
              >
                {doctorName} • {documentList.length} document(s)
              </p>
            </div>

            <button
              onClick={onClose}
              title="Close"
              style={{
                padding: "0.5rem",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "0.5rem",
                cursor: "pointer",
                color: "#6b7280",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.color = "#ef4444";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          {hasMultipleDocs && (
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                padding: "1rem 1.5rem",
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                overflowX: "auto",
              }}
            >
              {documentList.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentDocIndex(index)}
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "0.5rem",
                    border: "1px solid",
                    borderColor:
                      currentDocIndex === index ? "#a855f7" : "#e5e7eb",
                    backgroundColor:
                      currentDocIndex === index ? "#faf5ff" : "white",
                    color:
                      currentDocIndex === index ? "#9333ea" : "#6b7280",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: currentDocIndex === index ? 600 : 400,
                    transition: "all 0.2s",
                    whiteSpace: "nowrap",
                  }}
                >
                  Document {index + 1}
                </button>
              ))}
            </div>
          )}

          {/* Document Viewer */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "1.5rem",
              backgroundColor: "#f9fafb",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                padding: "2rem",
                minHeight: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {(() => {
                const doc = documentList[currentDocIndex];
                if (!doc) return <p>No document found</p>;
                const lowerDoc = doc.toLowerCase();

                if (lowerDoc.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)) {
                  return (
                    <img
                      src={doc}
                      alt={`Document ${currentDocIndex + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: "0.5rem",
                      }}
                    />
                  );
                }

                if (lowerDoc.endsWith(".pdf")) {
                  return (
                    <iframe
                      src={doc}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        minHeight: "600px",
                      }}
                      title={`Document ${currentDocIndex + 1}`}
                    />
                  );
                }

                return <div>{doc}</div>;
              })()}
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "0.75rem 1.5rem",
              borderTop: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              borderBottomLeftRadius: "1rem",
              borderBottomRightRadius: "1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: 0 }}>
              If the document doesn't load, try opening it in a new tab
            </p>
            {hasMultipleDocs && (
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <button
                  onClick={() => setCurrentDocIndex(Math.max(0, currentDocIndex - 1))}
                  disabled={currentDocIndex === 0}
                  style={{
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #e5e7eb",
                    borderRadius: "0.375rem",
                    backgroundColor: "white",
                    cursor: currentDocIndex === 0 ? "not-allowed" : "pointer",
                    opacity: currentDocIndex === 0 ? 0.5 : 1,
                  }}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  {currentDocIndex + 1} of {documentList.length}
                </span>
                <button
                  onClick={() =>
                    setCurrentDocIndex(
                      Math.min(documentList.length - 1, currentDocIndex + 1)
                    )
                  }
                  disabled={currentDocIndex === documentList.length - 1}
                  style={{
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #e5e7eb",
                    borderRadius: "0.375rem",
                    backgroundColor: "white",
                    cursor:
                      currentDocIndex === documentList.length - 1
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      currentDocIndex === documentList.length - 1 ? 0.5 : 1,
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
